import { computed, reactive } from '@vue/composition-api'
import Logger from '@/util/logger/Logger'
import DeviceInfo from '@/util/DeviceInfo'
import Const from '@/util/Const'
import StoreUtil from '@/store/StoreUtil'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import I18n from '@/locales/I18n'
import UserStore from '@/store/stores/pageStore/common/UserStore'
import { InAppPurchaseContractType } from '@/store/stores/collectionModule/documents/contractInfo/ContractInfoDocument'

/**
 * デバイスプラットフォームの型
 */
type PlatformType = CdvPurchase.Platform.APPLE_APPSTORE | CdvPurchase.Platform.GOOGLE_PLAY

/**
 * CdvPurchase.Productの型を拡張
 */
type ExtendedProductType = CdvPurchase.Product & {
  // サブスクリプションの定価
  listPrice: string
  // サブスクリプションの定価（数値）
  listPriceMicros: number
}

/**
 * アプリ内課金 表示用商品情報の型
 */
export type InAppPurchaseSubscriptionType = {
  annual: ExtendedProductType | undefined
  monthly: ExtendedProductType | undefined
}

/**
 * アプリ内課金処理のHook
 */
export default function useInAppPurchase() {
  const mypagePageStore = StoreUtil.useStore('MypagePageStore')
  const { store, ProductType, Platform } = CdvPurchase

  const state = reactive({
    // 定期購入商品情報
    subscriptions: {
      annual: undefined,
      monthly: undefined,
    } as InAppPurchaseSubscriptionType,
    // 対象のプラットフォーム（iOS または Android）
    targetPlatform: undefined as PlatformType | undefined,
    // 年額プラン定期購入商品の製品ID
    annualSubscriptionProductId: '',
    // 月額プラン定期購入商品の製品ID
    monthlySubscriptionProductId: '',
  })

  /** プラットフォーム */
  if (DeviceInfo.isiOS()) {
    // iOS
    state.targetPlatform = Platform.APPLE_APPSTORE
    if (process.env.VUE_APP_DISPLAY_IOS_IN_APP_PURCHASE_TEST_PLAN === 'true') {
      // iOSのテスト用プランを表示する場合
      // todo: トライアルプランをリリース後はこの処理は不要になる
      state.annualSubscriptionProductId = Const.IN_APP_PURCHASE_SUBSC_PRODUCT_ID.IOS.ANNUAL_TEST
      state.monthlySubscriptionProductId = Const.IN_APP_PURCHASE_SUBSC_PRODUCT_ID.IOS.MONTHLY_TEST
    } else {
      state.annualSubscriptionProductId = Const.IN_APP_PURCHASE_SUBSC_PRODUCT_ID.IOS.ANNUAL
      state.monthlySubscriptionProductId = Const.IN_APP_PURCHASE_SUBSC_PRODUCT_ID.IOS.MONTHLY
    }
  }
  if (DeviceInfo.isAndroid()) {
    // Android
    state.targetPlatform = Platform.GOOGLE_PLAY
    state.annualSubscriptionProductId = Const.IN_APP_PURCHASE_SUBSC_PRODUCT_ID.ANDROID.ANNUAL
    state.monthlySubscriptionProductId = Const.IN_APP_PURCHASE_SUBSC_PRODUCT_ID.ANDROID.MONTHLY
  }

  /** SFgo定期購入商品 */
  const sfgoSubscriptions = computed({
    get: () => <InAppPurchaseSubscriptionType>state.subscriptions,
    set: (val: InAppPurchaseSubscriptionType) => {
      state.subscriptions = val
    },
  })

  /**
   * restore関数を無効化
   */
  const storekit = Object.getPrototypeOf(store)
  storekit.originalRestore = storekit.restore
  if (DeviceInfo.isiOS() && localStorage) {
    /**
     * StoreKit(iOS)がある場合、refresh時のrestoreを防ぐため、restore関数を無効化する
     * https://qiita.com/s-suefusa/items/8da126510d0263dd1e60#ios%E3%81%AE%E3%83%AA%E3%82%B9%E3%83%88%E3%82%A2%E3%81%8C%E5%8E%84%E4%BB%8Bfor-ios
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    storekit.restore = () => {}
  }

  /**
   * 定価を取得
   * @param subscription ストアから取得したサブスクリプション情報
   */
  const getListPrice = (subscription: CdvPurchase.Product) => {
    // offersから定価を取得
    const subscriptionPricing = subscription?.offers
      ?.flatMap((offer) => offer.pricingPhases ?? [])
      .find((ph) => ph.paymentMode === 'PayAsYouGo')
    const listPrice = subscriptionPricing?.price ?? ''
    const listPriceMicros = subscriptionPricing?.priceMicros ?? 0

    return {
      listPrice,
      listPriceMicros,
    }
  }

  /**
   * UIをリフレッシュ
   */
  const refreshUI = () => {
    const annualSubscription = store.get(state.annualSubscriptionProductId, state.targetPlatform)
    const monthlySubscription = store.get(state.monthlySubscriptionProductId, state.targetPlatform)

    if (annualSubscription) {
      const { listPrice, listPriceMicros } = getListPrice(annualSubscription)
      // 年額プランの商品情報をセット
      sfgoSubscriptions.value.annual = Object.assign(
        Object.create(Object.getPrototypeOf(annualSubscription)),
        annualSubscription,
        {
          listPrice,
          listPriceMicros,
        },
      )
    }
    if (monthlySubscription) {
      const { listPrice, listPriceMicros } = getListPrice(monthlySubscription)
      // 月額プランの商品情報をセット
      sfgoSubscriptions.value.monthly = Object.assign(
        Object.create(Object.getPrototypeOf(monthlySubscription)),
        monthlySubscription,
        {
          listPrice,
          listPriceMicros,
        },
      )
    }
  }

  /**
   * 商品を購入
   */
  const purchaseSubscription = async (productId: string) => {
    const product = store.get(productId, state.targetPlatform)

    // 組織情報をサーバー通知で受け取れるようにする
    let additionalData
    const organizationId = UserStore.value.user.value._organization ?? ''
    const userId = UserStore.value.user.value.id ?? ''
    if (DeviceInfo.isiOS()) {
      store.applicationUsername = organizationId
    } else if (DeviceInfo.isAndroid()) {
      additionalData = {
        googlePlay: {
          accountId: organizationId,
          profileId: userId,
        },
      }
    }

    return product
      ?.getOffer()
      ?.order(additionalData)
      .then((error) => {
        if (error) {
          Logger.error(
            `useInAppPurchase#purchaseSubscription: error code: ${error?.code}. error message: ${error?.message}`,
          )
          return { isSuccess: false }
        }
        return { isSuccess: true }
      })
  }

  /**
   * 購入完了処理（iOS）
   * NOTE: 商品購入時、リストア時にfinishPurchaseが複数回呼ばれてしまうため、このメソッド内で契約情報の更新処理を呼ばないようにしている
   */
  const finishPurchase = async (transaction: CdvPurchase.Transaction) => {
    // iOSの場合transactionIdをセット
    mypagePageStore.purchaseSubscriptionUuid.value = transaction.transactionId

    // iOSの場合、購入完了後にトランザクションを閉じる
    await transaction.finish()

    if (localStorage) {
      /**
       * iOSのローカルストレージのレシート情報は使用しないため破棄する
       * https://qiita.com/s-suefusa/items/8da126510d0263dd1e60#%E3%83%AD%E3%83%BC%E3%82%AB%E3%83%AB%E3%82%B9%E3%83%88%E3%83%AC%E3%83%BC%E3%82%B8%E3%81%8C%E3%83%91%E3%83%B3%E3%82%AF%E3%81%99%E3%82%8Bfor-ios
       */
      localStorage.removeItem('sk_receiptForProduct')
      localStorage.removeItem('sk_receiptForTransaction')
      localStorage.removeItem('sk_appStoreReceipt')
    }
  }

  /**
   * 購入完了処理（Android）
   * NOTE: 商品購入時、リストア時にfinishPurchaseが複数回呼ばれてしまうため、このメソッド内で契約情報の更新処理を呼ばないようにしている
   */
  const prepareFinishPurchase = async (transaction: CdvPurchase.Transaction) => {
    // Androidの場合purchaseId（=purchaseToken）をセット
    mypagePageStore.purchaseSubscriptionUuid.value = transaction.purchaseId ?? ''

    // アプリ内課金の通知を受け取った後にfinish処理を実行するためするため、トランザクション情報をストアに保存
    mypagePageStore.inAppPurchaseTransaction.value = transaction
  }

  /**
   * リストア処理
   */
  const restore = async () =>
    // finishPurchaseが実行され、プラン情報が更新される
    store.restorePurchases().then(() => {
      Logger.info(`useInAppPurchase#restore: purchase has been restored.`)
    })

  /**
   * 表示する商品情報データを設定
   */
  const setDisplaySubscriptionData = async () => {
    // 購入ステータスを更新
    await store.update()
    // UIを更新
    refreshUI()
  }

  /**
   * 初期化処理
   */
  const initInAppPurchase = async () => {
    if (state.targetPlatform) {
      store.register([
        {
          id: state.annualSubscriptionProductId,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: state.targetPlatform,
        },
        {
          id: state.monthlySubscriptionProductId,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: state.targetPlatform,
        },
      ])
    }

    await store.initialize([Platform.GOOGLE_PLAY, Platform.APPLE_APPSTORE])
    // 購入ステータスを更新
    await store.update()
    store.verbosity = CdvPurchase.LogLevel.DEBUG

    refreshUI()

    /**
     * エラーハンドラを登録
     */
    store.error((error) => {
      Logger.error(
        `useInAppPurchase#onDeviceReady: error code: ${error.code}. error message: ${error.message}`,
      )
      if (error.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
        // 購入をキャンセルした場合もエラー処理が発火する。キャンセルした場合はエラーメッセージを表示する必要がないと思うため、表示しないようにした。
        return
      }
      MessageDialogStore.value.open({
        title: I18n.tc(
          'MypagePage.MypageSwitchToPaidPlanPage.errors.inAppPurchaseUnexpectedError.title',
        ),
        message: I18n.t(
          'MypagePage.MypageSwitchToPaidPlanPage.errors.inAppPurchaseUnexpectedError.message',
          {
            errorCode: error.code,
            errorMessage: error.message,
          },
        ).toString(),
      })
    })

    /**
     * 商品にコールバックを設定
     */
    store
      .when()
      .productUpdated(refreshUI) // 商品に変更があった
      .approved((receipt) => {
        // 商品が承認済みになった
        if (DeviceInfo.isiOS()) {
          return finishPurchase(receipt)
        }
        if (DeviceInfo.isAndroid()) {
          return prepareFinishPurchase(receipt)
        }
        return undefined
      })
  }

  /**
   * トライアルプラン適用可能かどうか。適用可能の場合、trueを返す。
   * @param contractInfoInAppPurchase 契約情報が持っているアプリ内課金に関する情報
   */
  const eligibleTrialPlan = (
    contractInfoInAppPurchase: InAppPurchaseContractType | null | undefined,
  ) => {
    /**
     * トライアル対象/対象外の場合について、それぞれ以下の情報が返ってくる。
     * トライアル対象の場合: price「￥0（=通貨が円の場合）」priceMicros「0」
     * トライアル対象外の場合: price「￥通常価格（=通貨が円の場合）」priceMicros「通常価格の数値」
     * ※ただし、以下の理由からpriceMicrosの値でトライアル対象/対象外を判定するのは危険であるため、SFgoの契約情報を参照する条件と組み合わせて使用する。
     *
     * Android: Google Play Billing Library におけるバグやキャッシュの影響で、本番環境でも一時的にpriceMicrosが0になることがある。バグ報告については下記リンクを参照。
     * https://www.reddit.com/r/androiddev/comments/1bt7glc/warning_google_play_billing_library_bug_causing/?utm_source=chatgpt.com
     *
     * iOS: Sandbox環境では、トライアル対象外の場合もpriceMicrosが0になる。Sandbox特有の挙動である可能性があるが、本番でも発生するかもしれない。
     */
    const annualPlanPrice = sfgoSubscriptions.value.annual?.pricing?.priceMicros ?? 0
    const monthlyPlanPrice = sfgoSubscriptions.value.monthly?.pricing?.priceMicros ?? 0
    const trialAvailable = annualPlanPrice === 0 || monthlyPlanPrice === 0

    if (DeviceInfo.isAndroid()) {
      /** Androidの場合の処理 */
      return !contractInfoInAppPurchase?.googlePlay?.purchaseToken && trialAvailable
    }

    /** iOSの場合の処理 */
    return !contractInfoInAppPurchase?.appStore?.transactionId && trialAvailable
  }

  return {
    store,
    sfgoSubscriptions,
    purchaseSubscription,
    restore,
    setDisplaySubscriptionData,
    initInAppPurchase,
    eligibleTrialPlan,
  }
}
